export type ChatRole = "system" | "user" | "assistant";

export interface ChatMessage {
  role: ChatRole;
  content: string;
}

export interface ChatCompletionResponse {
  id: string;
  model: string;
  choices: Array<{
    index: number;
    message: { role: ChatRole; content: string };
    finish_reason: string | null;
  }>;
  usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
}

export type ErrorType =
  | "grammar"
  | "vocab"
  | "word_order"
  | "article"
  | "preposition"
  | "tense"
  | "spelling"
  | "pronunciation"
  | "other";

export interface AnalyzedError {
  span: string;
  type: ErrorType;
  category: string;
  explanation: string;
}

export type CefrLevel = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

export interface TurnAnalysis {
  corrected: string;
  errors: AnalyzedError[];
  vocabulary_used: string[];
  estimated_level: CefrLevel;
  topic: string;
}

export interface TurnResponse {
  reply: string;
  analysis: TurnAnalysis | null;
}

export interface SessionStartResponse {
  session_id: string;
  user_id: string;
}

export interface SessionSummary {
  session_id: string;
  user_id: string;
  started_at: number;
  ended_at: number;
  duration_s: number;
  turn_count_user: number;
  turn_count_assistant: number;
  error_count: number;
  errors_by_category: Array<{ category: string; type: string; count: number; example: string }>;
  new_vocab: string[];
  estimated_level: string | null;
  top_recurring_errors: Array<{
    category: string;
    type: string;
    count: number;
    explanation: string;
  }>;
}

export interface UserStats {
  user_id: string;
  total_sessions: number;
  total_turns_user: number;
  total_errors: number;
  current_level: string | null;
  top_recurring_errors: Array<{
    category: string;
    type: string;
    count: number;
    explanation: string;
  }>;
}

import { getAccessToken } from "./auth";

function fetchOpts(extra: RequestInit = {}): RequestInit {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    ...((extra.headers as Record<string, string>) ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return { credentials: "include", ...extra, headers };
}

export async function sendChat(messages: ChatMessage[]): Promise<string> {
  const res = await fetch("/api/chat", {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
  }
  const data = (await res.json()) as ChatCompletionResponse;
  const content = data.choices?.[0]?.message?.content;
  if (typeof content !== "string") throw new Error("No content in response");
  return content;
}

export async function analyzeUtteranceApi(
  text: string,
  opts: { sessionId?: string; targetLang?: string; nativeLang?: string; recentTurns?: ChatMessage[] } = {},
): Promise<TurnAnalysis | null> {
  try {
    const res = await fetch("/api/analyze", {
      ...fetchOpts(),
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        sessionId: opts.sessionId,
        targetLang: opts.targetLang ?? "English",
        nativeLang: opts.nativeLang ?? "Polish",
        recentTurns: opts.recentTurns,
      }),
    });
    if (!res.ok) return null;
    const { analysis } = (await res.json()) as { analysis: TurnAnalysis | null };
    return analysis;
  } catch {
    return null;
  }
}

export async function sendTurn(
  messages: ChatMessage[],
  opts: { sessionId?: string; targetLang?: string; nativeLang?: string } = {},
): Promise<TurnResponse> {
  const res = await fetch("/api/turn", {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      messages,
      sessionId: opts.sessionId,
      targetLang: opts.targetLang ?? "English",
      nativeLang: opts.nativeLang ?? "Polish",
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
  }
  return (await res.json()) as TurnResponse;
}

export async function startSession(
  opts: { targetLang?: string; nativeLang?: string } = {},
): Promise<SessionStartResponse> {
  const res = await fetch("/api/session/start", {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      targetLang: opts.targetLang ?? "English",
      nativeLang: opts.nativeLang ?? "Polish",
    }),
  });
  if (!res.ok) throw new Error(`session/start ${res.status}`);
  return (await res.json()) as SessionStartResponse;
}

export async function endSession(sessionId: string): Promise<SessionSummary> {
  const res = await fetch(`/api/session/${sessionId}/end`, {
    ...fetchOpts(),
    method: "POST",
  });
  if (!res.ok) throw new Error(`session/end ${res.status}`);
  return (await res.json()) as SessionSummary;
}

export async function getUserStats(): Promise<UserStats> {
  const res = await fetch("/api/me/stats", fetchOpts());
  if (!res.ok) throw new Error(`me/stats ${res.status}`);
  return (await res.json()) as UserStats;
}

export interface UserRow {
  id: string;
  name: string | null;
  target_lang: string;
  native_lang: string;
  level: CefrLevel | null;
  goal: string | null;
  goal_track?: string | null;
  profession?: string | null;
  interests?: string[];
  target_level?: string | null;
  exam_name?: string | null;
  exam_date?: number | null;
  created_at: number;
  last_seen_at: number;
  kc_sub?: string | null;
  email?: string | null;
  is_anonymous?: number;
  plan?: string;
  trial_ends_at?: number | null;
}

export async function getMe(): Promise<{ user: UserRow | null; isAdmin: boolean }> {
  const res = await fetch("/api/me", fetchOpts());
  if (!res.ok) throw new Error(`me ${res.status}`);
  return (await res.json()) as { user: UserRow | null; isAdmin: boolean };
}

export interface LastSessionRecap {
  started_at: number;
  ended_at: number;
  narrative_summary: string;
  focus_for_next_time: string;
  personal_facts: string[];
  error_count_total: number;
}

export async function getLastSessionRecap(): Promise<LastSessionRecap | null> {
  const res = await fetch("/api/me/last-session", fetchOpts());
  if (!res.ok) return null;
  const { recap } = (await res.json()) as { recap: LastSessionRecap | null };
  return recap;
}

// ---------- Vocab SRS ----------

export interface DueCard {
  id: string;
  lemma: string;
  glossPl: string | null;
  example: string | null;
  state: string;
  dueAt: number;
  lapses: number;
}

export interface VocabStats {
  total: number;
  due_now: number;
  new_cards: number;
  learning: number;
  review: number;
  due_today: number;
}

export interface ReviewResult {
  id: string;
  state: string;
  dueAt: number;
  stability: number;
  difficulty: number;
}

export async function getDueCards(limit = 20): Promise<DueCard[]> {
  const res = await fetch(`/api/vocab/due?limit=${limit}`, fetchOpts());
  if (!res.ok) return [];
  const { cards } = (await res.json()) as { cards: DueCard[] };
  return cards;
}

export async function getVocabStats(): Promise<VocabStats | null> {
  const res = await fetch("/api/vocab/stats", fetchOpts());
  if (!res.ok) return null;
  return (await res.json()) as VocabStats;
}

export async function resetOnboarding(): Promise<boolean> {
  const res = await fetch("/api/me/reset-onboarding", { ...fetchOpts(), method: "POST" });
  return res.ok;
}

// ---------- Pronunciation scoring (Speechace via /api/pronunciation/score) ----------

export interface PronunciationWord {
  word: string;
  score: number;
  issues: Array<{ phone: string; score: number; sounded_like: string | null }>;
}

export interface PronunciationResult {
  overall: number;
  text: string;
  words: PronunciationWord[];
  ielts_pronunciation?: number;
  cefr_pronunciation?: string;
  speech_rate?: number;
}

// ---------- Exam mock ----------

export interface ExamBands {
  fluency_coherence: number;
  lexical_resource: number;
  grammatical_range: number;
  pronunciation: number;
  overall: number;
}

export interface ExamPart {
  name: "part1" | "part2" | "part3";
  prompt: string;
  hint?: string;
  response?: string;
  bands?: ExamBands;
  feedback?: string;
}

export interface ExamSnapshot {
  id: string;
  exam_type: string;
  status: "in_progress" | "completed" | "abandoned";
  parts: ExamPart[];
  overall_band: number | null;
  feedback: string | null;
}

export async function startIeltsExam(): Promise<ExamSnapshot | null> {
  const res = await fetch("/api/exam/ielts/start", { ...fetchOpts(), method: "POST" });
  if (!res.ok) return null;
  return (await res.json()) as ExamSnapshot;
}

export async function submitExamPart(id: string, part: "part1" | "part2" | "part3", response: string): Promise<ExamSnapshot | null> {
  const res = await fetch(`/api/exam/${encodeURIComponent(id)}/submit-part`, {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ part, response }),
  });
  if (!res.ok) return null;
  return (await res.json()) as ExamSnapshot;
}

export async function finishExam(id: string): Promise<ExamSnapshot | null> {
  const res = await fetch(`/api/exam/${encodeURIComponent(id)}/finish`, {
    ...fetchOpts(),
    method: "POST",
  });
  if (!res.ok) return null;
  return (await res.json()) as ExamSnapshot;
}

// ---------- Sessions history ----------

export interface SessionSummaryRow {
  id: string;
  started_at: number;
  ended_at: number | null;
  target_lang: string;
  turn_count: number;
  error_count: number;
  narrative_summary: string | null;
  focus_for_next_time: string | null;
  estimated_level: string | null;
}

export interface SessionTurnRow {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
  created_at: number;
  analysis: TurnAnalysis | null;
}

export interface SessionTranscript {
  id: string;
  started_at: number;
  ended_at: number | null;
  turns: SessionTurnRow[];
}

export async function listSessions(limit = 20, offset = 0): Promise<SessionSummaryRow[]> {
  const res = await fetch(`/api/me/sessions?limit=${limit}&offset=${offset}`, fetchOpts());
  if (!res.ok) return [];
  const { sessions } = (await res.json()) as { sessions: SessionSummaryRow[] };
  return sessions;
}

export async function getSessionTranscript(id: string): Promise<SessionTranscript | null> {
  const res = await fetch(`/api/me/sessions/${encodeURIComponent(id)}/transcript`, fetchOpts());
  if (!res.ok) return null;
  return (await res.json()) as SessionTranscript;
}

// ---------- Streak ----------

export interface StreakInfo {
  current: number;
  best: number;
  active_today: boolean;
  at_risk: boolean;
  last_active: number | null;
}

export async function getStreak(): Promise<StreakInfo | null> {
  const res = await fetch("/api/me/streak", fetchOpts());
  if (!res.ok) return null;
  return (await res.json()) as StreakInfo;
}

// ---------- Re-placement ----------

export interface ReplacementStatus {
  due: boolean;
  last_placement_at: number | null;
  days_since: number | null;
  current_level: string | null;
  target_level: string | null;
}

export async function getReplacementStatus(): Promise<ReplacementStatus | null> {
  const res = await fetch("/api/me/replacement-status", fetchOpts());
  if (!res.ok) return null;
  return (await res.json()) as ReplacementStatus;
}

// ---------- CEFR sub-skill profile ----------

export interface SkillProfile {
  level: string | null;
  target_level: string | null;
  generated_at: number;
  dimensions: {
    vocab_range:   { score: number; total_lemmas: number; target_for_level: number };
    accuracy:      { score: number; turns_window: number; error_rate: number };
    pronunciation: { score: number; samples_window: number };
    listening:     { score: number; attempts_window: number };
    engagement:    { score: number; sessions_window: number; user_turns_window: number };
    unit_progress: { score: number; completed: number; available: number };
  };
}

export async function getSkillProfile(): Promise<SkillProfile | null> {
  const res = await fetch("/api/me/skills", fetchOpts());
  if (!res.ok) return null;
  return (await res.json()) as SkillProfile;
}

// ---------- Listening comprehension ----------

export interface ListeningExercise {
  id: string;
  slug: string;
  level: string;
  track: string;
  title: string;
  description: string | null;
  transcript: string;
  durationSec: number;
  voiceId: string | null;
  questions: Array<{ q: string; hint?: string }>;
}

export interface ListeningScore {
  per_question: Array<{ score: number; correct: boolean; comment: string }>;
  overall: number;
  feedback: string;
}

export async function getNextListening(): Promise<ListeningExercise | null> {
  const res = await fetch("/api/listening/next", fetchOpts());
  if (!res.ok) return null;
  const { exercise } = (await res.json()) as { exercise: ListeningExercise | null };
  return exercise;
}

export async function getListening(slug: string): Promise<ListeningExercise | null> {
  const res = await fetch(`/api/listening/${encodeURIComponent(slug)}`, fetchOpts());
  if (!res.ok) return null;
  const { exercise } = (await res.json()) as { exercise: ListeningExercise };
  return exercise;
}

export function listeningAudioUrl(slug: string): string {
  return `/api/listening/${encodeURIComponent(slug)}/audio`;
}

export async function submitListening(slug: string, answers: string[]): Promise<ListeningScore | null> {
  const res = await fetch(`/api/listening/${encodeURIComponent(slug)}/submit`, {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ answers }),
  });
  if (!res.ok) return null;
  const { score } = (await res.json()) as { score: ListeningScore | null };
  return score;
}

// ---------- Homework ----------

export interface HomeworkTask {
  id: string;
  kind: "write" | "vocab" | "reflect" | "listen";
  prompt: string;
  hint: string | null;
  response: string | null;
  status: string;
  created_at: number;
  completed_at: number | null;
  due_at: number | null;
}

export async function getHomework(): Promise<HomeworkTask[]> {
  const res = await fetch("/api/homework", fetchOpts());
  if (!res.ok) return [];
  const { tasks } = (await res.json()) as { tasks: HomeworkTask[] };
  return tasks;
}

export async function submitHomework(id: string, response: string): Promise<boolean> {
  const res = await fetch(`/api/homework/${encodeURIComponent(id)}/submit`, {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ response }),
  });
  return res.ok;
}

export async function skipHomework(id: string): Promise<boolean> {
  const res = await fetch(`/api/homework/${encodeURIComponent(id)}/skip`, {
    ...fetchOpts(),
    method: "POST",
  });
  return res.ok;
}

// ---------- Curriculum ----------

export interface CurriculumUnit {
  id: string;
  slug: string;
  level: string;
  track: string;
  title: string;
  topic: string;
  grammarFocus: string;
  targetVocab: string[];
  dialogModel: string;
  drillItems: string[];
  durationMin: number;
}

export interface StartUnitResponse {
  unit: CurriculumUnit;
  mode: "targeted" | "drill";
  prompt_block: string;
}

export async function getNextUnit(): Promise<CurriculumUnit | null> {
  const res = await fetch("/api/curriculum/next", fetchOpts());
  if (!res.ok) return null;
  const { unit } = (await res.json()) as { unit: CurriculumUnit | null };
  return unit;
}

export async function startUnit(slug: string, mode: "targeted" | "drill"): Promise<StartUnitResponse | null> {
  const res = await fetch("/api/curriculum/start", {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug, mode }),
  });
  if (!res.ok) return null;
  return (await res.json()) as StartUnitResponse;
}

export async function completeUnit(slug: string, score?: number): Promise<boolean> {
  const res = await fetch(`/api/curriculum/${encodeURIComponent(slug)}/complete`, {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ score }),
  });
  return res.ok;
}

export async function scorePronunciation(
  audio: Blob,
  expectedText: string,
): Promise<PronunciationResult | null> {
  const fd = new FormData();
  fd.append("text", expectedText);
  fd.append("audio", audio, "utterance.webm");

  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch("/api/pronunciation/score", {
    method: "POST",
    credentials: "include",
    headers,
    body: fd,
  });
  if (!res.ok) return null;
  return (await res.json()) as PronunciationResult;
}

export async function reviewCard(id: string, rating: 1 | 2 | 3 | 4): Promise<ReviewResult | null> {
  const res = await fetch(`/api/vocab/${id}/review`, {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rating }),
  });
  if (!res.ok) return null;
  return (await res.json()) as ReviewResult;
}

export interface AdminUserRow {
  id: string;
  email: string | null;
  name: string | null;
  role: string;
  level: string | null;
  plan: string;
  is_anonymous: number;
  kc_sub: string | null;
  created_at: number;
  last_seen_at: number;
}

export async function listAdminUsers(): Promise<{ users: AdminUserRow[] }> {
  const res = await fetch("/api/admin/users", fetchOpts());
  if (!res.ok) throw new Error(`admin/users ${res.status}`);
  return (await res.json()) as { users: AdminUserRow[] };
}

export async function updateProfile(patch: {
  name?: string;
  target_lang?: string;
  native_lang?: string;
  goal?: string;
  goal_track?: string;
  profession?: string;
  interests?: string[];
  target_level?: string;
  exam_name?: string;
  exam_date?: number;
}): Promise<{ user: UserRow }> {
  const res = await fetch("/api/me/profile", {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`profile ${res.status}`);
  return (await res.json()) as { user: UserRow };
}

export interface PlacementResult {
  level: CefrLevel;
  detected_levels: CefrLevel[];
  total_errors: number;
  user: UserRow;
}

export async function submitPlacement(
  answers: Array<{ question: string; answer: string }>,
  opts: { target_lang?: string; native_lang?: string } = {},
): Promise<PlacementResult> {
  const res = await fetch("/api/me/placement", {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      answers,
      target_lang: opts.target_lang ?? "English",
      native_lang: opts.native_lang ?? "Polish",
    }),
  });
  if (!res.ok) throw new Error(`placement ${res.status}`);
  return (await res.json()) as PlacementResult;
}


export async function linkAnonymous(): Promise<{ merged: boolean; reason?: string }> {
  const res = await fetch("/api/auth/link", { ...fetchOpts(), method: "POST" });
  if (!res.ok) return { merged: false, reason: `HTTP ${res.status}` };
  return (await res.json()) as { merged: boolean; reason?: string };
}
